import React, { useState, useEffect, useCallback } from 'react';
import { supabase, TruckVisit, TruckStatus, Plant, LogisticCompany, Truck, STATUS_LABELS, STATUS_COLORS, STATUS_ORDER, SHIFT_OPTIONS } from './lib/supabase';
import { LogIn, LogOut, Plus, X, Clock, Truck as TruckIcon, Filter, ChevronDown, Download, Calendar, Settings, Building, Building2, Users, ArrowLeft, Save, Trash2, Edit as EditIcon } from 'lucide-react';

// Profile type with updated roles
type UserRole = 'viewer' | 'editor' | 'admin';

interface Profile {
  id: string;
  full_name: string | null;
  phone: string | null;
  role: UserRole;
  plant_id: string | null;
  created_at: string;
}

const ROLE_HIERARCHY: Record<UserRole, number> = { viewer: 0, editor: 1, admin: 2 };

const canEdit = (role: UserRole) => ROLE_HIERARCHY[role] >= ROLE_HIERARCHY.editor;
const canAdmin = (role: UserRole) => ROLE_HIERARCHY[role] >= ROLE_HIERARCHY.admin;
const canView = (role: UserRole) => ROLE_HIERARCHY[role] >= ROLE_HIERARCHY.viewer;

// ------------------ Auth ------------------
const ALLOWED_EMAIL_DOMAIN = import.meta.env.VITE_ALLOWED_EMAIL_DOMAIN || 'yourcompany.com';

function AuthPage({ onDemo }: { onDemo: () => void }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleLogin = async () => {
    if (!email.toLowerCase().endsWith(`@${ALLOWED_EMAIL_DOMAIN.toLowerCase()}`)) {
      alert(`Only @${ALLOWED_EMAIL_DOMAIN} email addresses are allowed`);
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    setLoading(false);
    if (error) {
      alert('Error: ' + error.message);
    } else {
      setSent(true);
    }
  };

  if (sent) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <TruckIcon size={48} className="auth-icon" />
          <h2>Check your email</h2>
          <p>We sent a magic link to <strong>{email}</strong>. Click the link to sign in.</p>
          <button className="btn btn-secondary" onClick={() => setSent(false)}>
            Use a different email
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <TruckIcon size={48} className="auth-icon" />
        <h1>Truck Tracker</h1>
        <p className="auth-subtitle">Sign in to access the dashboard</p>
        <p className="auth-domain-hint">Only @{ALLOWED_EMAIL_DOMAIN} emails allowed</p>
        <input
          type="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="input"
          autoFocus
        />
        <button className="btn btn-primary" onClick={handleLogin} disabled={loading || !email}>
          {loading ? 'Sending...' : 'Send Magic Link'}
        </button>
        <div className="demo-divider">or</div>
        <button className="btn btn-secondary" onClick={onDemo}>
          Try Demo (Read-Only)
        </button>
      </div>
    </div>
  );
}

// ------------------ Entry Form ------------------
function EntryForm({
  plants,
  logisticCompanies,
  trucks,
  onClose,
  onSaved,
  editVisit,
  userRole,
}: {
  plants: Plant[];
  logisticCompanies: LogisticCompany[];
  trucks: Truck[];
  onClose: () => void;
  onSaved: () => void;
  editVisit?: TruckVisit | null;
  userRole: UserRole | null;
}) {
  const [form, setForm] = useState({
    visit_date: editVisit?.visit_date?.split('T')[0] || new Date().toISOString().split('T')[0],
    plant_id: editVisit?.plant_id || '',
    truck_id: editVisit?.truck_id || '',
    contact_no: editVisit?.contact_no || '',
    destination: editVisit?.destination || '',
    logistic_company_id: editVisit?.logistic_company_id || '',
    status: (editVisit?.status || 'scheduled') as TruckStatus,
    time_in: editVisit?.time_in?.split('T')[1]?.slice(0, 5) || '',
    time_out: editVisit?.time_out?.split('T')[1]?.slice(0, 5) || '',
    issue_note: editVisit?.issue_note || '',
    driver_name: editVisit?.driver_name || '',
    forklift_operator_name: editVisit?.forklift_operator_name || '',
    shift_loaded: editVisit?.shift_loaded || '',
  });
  const [saving, setSaving] = useState(false);
  const [newTruck, setNewTruck] = useState('');
  const [newCompany, setNewCompany] = useState('');
  const [companySearch, setCompanySearch] = useState('');

  const filteredCompanies = logisticCompanies.filter(c =>
    c.name.toLowerCase().includes(companySearch.toLowerCase())
  );

  // Viewers cannot use the form - read only
  if (!canEdit(userRole)) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{editVisit ? 'View Visit' : 'New Truck Entry'}</h2>
            <button className="icon-btn" onClick={onClose}><X size={20} /></button>
          </div>
          <div className="modal-body">
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>
              {userRole === 'viewer' ? 'View-only access. Contact admin for editor role.' : 'Please sign in to create or edit entries.'}
            </p>
          </div>
          <div className="modal-footer">
            <button className="btn btn-primary" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  const handleSave = async () => {
    if (!form.plant_id || !form.truck_id || !form.destination) {
      alert('Please fill: Plant, Truck, and Destination');
      return;
    }

    setSaving(true);
    const payload: any = {
      visit_date: form.visit_date,
      plant_id: form.plant_id,
      truck_id: form.truck_id,
      contact_no: form.contact_no,
      destination: form.destination,
      logistic_company_id: form.logistic_company_id,
      status: form.status,
      issue_note: form.status === 'issue' ? form.issue_note : null,
      driver_name: form.driver_name,
      forklift_operator_name: form.forklift_operator_name,
      shift_loaded: form.shift_loaded,
    };

    if (form.time_in) {
      payload.time_in = `${form.visit_date}T${form.time_in}:00+03:00`;
    }
    if (form.time_out) {
      payload.time_out = `${form.visit_date}T${form.time_out}:00+03:00`;
    }

    const { error } = editVisit
      ? await supabase.from('truck_visits').update(payload).eq('id', editVisit.id)
      : await supabase.from('truck_visits').insert(payload);

    setSaving(false);
    if (error) {
      alert('Error: ' + error.message);
    } else {
      onSaved();
      onClose();
    }
  };

  const addNewTruck = async () => {
    if (!newTruck.trim()) return;
    const { data, error } = await supabase.from('trucks').insert({ truck_no: newTruck.trim().toUpperCase() }).select();
    if (error) { alert('Error: ' + error.message); return; }
    setNewTruck('');
    onSaved(); // refresh
  };

  const addNewCompany = async () => {
    if (!newCompany.trim()) return;
    const { data, error } = await supabase.from('logistic_companies').insert({ name: newCompany.trim() }).select();
    if (error) { alert('Error: ' + error.message); return; }
    setNewCompany('');
    onSaved(); // refresh
  };

  const selectedTruck = trucks.find(t => t.id === form.truck_id);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{editVisit ? 'Edit Visit' : 'New Truck Entry'}</h2>
          <button className="icon-btn" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="form-grid">
          <div className="field">
            <label>Date</label>
            <input type="date" className="input" value={form.visit_date}
              onChange={(e) => setForm({ ...form, visit_date: e.target.value })} />
          </div>

          <div className="field">
            <label>Plant</label>
            <select className="input" value={form.plant_id}
              onChange={(e) => setForm({ ...form, plant_id: e.target.value })}>
              <option value="">Select plant</option>
              {plants.map(p => <option key={p.id} value={p.id}>{p.code} - {p.name}</option>)}
            </select>
          </div>

          <div className="field">
            <label>Truck No</label>
            <div className="field-row">
              <select className="input" value={form.truck_id}
                onChange={(e) => { setForm({ ...form, truck_id: e.target.value }); const t = trucks.find(t => t.id === e.target.value); if (t?.logistic_company_id) setForm(f => ({ ...f, truck_id: e.target.value, logistic_company_id: t.logistic_company_id! })); }}>
                <option value="">Select truck</option>
                {trucks.map(t => <option key={t.id} value={t.id}>{t.truck_no}</option>)}
              </select>
              <input type="text" className="input" placeholder="Or add new truck"
                value={newTruck} onChange={(e) => setNewTruck(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addNewTruck()} />
              {newTruck && <button className="btn btn-sm btn-primary" onClick={addNewTruck}>Add</button>}
            </div>
          </div>

          <div className="field">
            <label>Contact No</label>
            <input type="tel" className="input" placeholder="Driver phone"
              value={form.contact_no} onChange={(e) => setForm({ ...form, contact_no: e.target.value })} />
          </div>

          <div className="field">
            <label>Destination</label>
            <input type="text" className="input" placeholder="e.g. Mumbai Plant"
              value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} />
          </div>

          <div className="field">
            <label>Logistic Company</label>
            <div className="field-row">
              <input type="text" className="input" placeholder="Search or type new"
                value={companySearch} onChange={(e) => setCompanySearch(e.target.value)} />
              {companySearch && !filteredCompanies.find(c => c.name === companySearch) && (
                <button className="btn btn-sm btn-primary" onClick={() => { setNewCompany(companySearch); addNewCompany(); }}>Add "{companySearch}"</button>
              )}
            </div>
            <select className="input" value={form.logistic_company_id}
              onChange={(e) => setForm({ ...form, logistic_company_id: e.target.value })}>
              <option value="">Select company</option>
              {filteredCompanies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          <div className="field">
            <label>Driver Name</label>
            <input type="text" className="input" placeholder="Driver full name"
              value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} />
          </div>

          <div className="field">
            <label>Forklift Operator</label>
            <input type="text" className="input" placeholder="Forklift operator name"
              value={form.forklift_operator_name} onChange={(e) => setForm({ ...form, forklift_operator_name: e.target.value })} />
          </div>

          <div className="field">
            <label>Shift Loaded</label>
            <select className="input" value={form.shift_loaded}
              onChange={(e) => setForm({ ...form, shift_loaded: e.target.value })}>
              <option value="">Select shift</option>
              {SHIFT_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="field">
            <label>Status</label>
            <select className="input" value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as TruckStatus })}>
              {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
          </div>

          {form.status === 'issue' && (
            <div className="field full-width">
              <label>Issue Note</label>
              <textarea className="input" rows={2} placeholder="Describe the issue..."
                value={form.issue_note} onChange={(e) => setForm({ ...form, issue_note: e.target.value })} />
            </div>
          )}

          <div className="field">
            <label>Time In</label>
            <input type="time" className="input" value={form.time_in}
              onChange={(e) => setForm({ ...form, time_in: e.target.value })} />
          </div>

          <div className="field">
            <label>Time Out</label>
            <input type="time" className="input" value={form.time_out}
              onChange={(e) => setForm({ ...form, time_out: e.target.value })} />
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : editVisit ? 'Update' : 'Save Entry'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------ Status Badge ------------------
function StatusBadge({ status }: { status: TruckStatus }) {
  return (
    <span className="status-badge" style={{ backgroundColor: STATUS_COLORS[status] + '20', color: STATUS_COLORS[status], borderColor: STATUS_COLORS[status] + '40' }}>
      <span className="status-dot" style={{ backgroundColor: STATUS_COLORS[status] }} />
      {STATUS_LABELS[status]}
    </span>
  );
}

// ------------------ Dashboard ------------------
function Dashboard({ onLogout, demoMode, userRole, onAdmin }: { onLogout: () => void; demoMode: boolean; userRole: UserRole | null; onAdmin: () => void }) {
  const [visits, setVisits] = useState<TruckVisit[]>([]);
  const [plants, setPlants] = useState<Plant[]>([]);
  const [logisticCompanies, setLogisticCompanies] = useState<LogisticCompany[]>([]);
  const [trucks, setTrucks] = useState<Truck[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editVisit, setEditVisit] = useState<TruckVisit | null>(null);
  const [filterStatus, setFilterStatus] = useState<TruckStatus | 'all'>('all');
  const [filterDate, setFilterDate] = useState(new Date().toISOString().split('T')[0]);
  const [showFilters, setShowFilters] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    const [visitsRes, plantsRes, companiesRes, trucksRes] = await Promise.all([
      supabase.from('truck_visits').select('*, plants(*), trucks(*), logistic_companies(*)').order('registered_at', { ascending: false }).limit(200),
      supabase.from('plants').select('*'),
      supabase.from('logistic_companies').select('*').order('name'),
      supabase.from('trucks').select('*').order('truck_no'),
    ]);
    if (visitsRes.data) setVisits(visitsRes.data as unknown as TruckVisit[]);
    if (plantsRes.data) setPlants(plantsRes.data);
    if (companiesRes.data) setLogisticCompanies(companiesRes.data);
    if (trucksRes.data) setTrucks(trucksRes.data);
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredVisits = visits.filter(v => {
    if (filterStatus !== 'all' && v.status !== filterStatus) return false;
    if (filterDate && v.visit_date !== filterDate) return false;
    return true;
  });

  const todayVisits = filteredVisits.filter(v => v.visit_date === new Date().toISOString().split('T')[0]);
  const loadedCount = filteredVisits.filter(v => v.status === 'loaded').length;
  const issueCount = filteredVisits.filter(v => v.status === 'issue' || v.status === 'delayed').length;
  const activeCount = filteredVisits.filter(v => !['departed', 'cancelled', 'loaded'].includes(v.status)).length;

  const closeForm = () => { setShowForm(false); setEditVisit(null); };
  const handleSaved = () => { loadData(); };

  const exportCSV = () => {
    const headers = ['Date', 'Plant', 'Truck No', 'Contact', 'Driver', 'Forklift Operator', 'Shift', 'Destination', 'Company', 'Status', 'Time In', 'Time Out'];
    const rows = filteredVisits.map(v => [
      v.visit_date,
      v.plants?.name || '',
      v.trucks?.truck_no || '',
      v.contact_no,
      v.driver_name,
      v.forklift_operator_name,
      v.shift_loaded,
      v.destination,
      v.logistic_companies?.name || '',
      STATUS_LABELS[v.status],
      v.time_in ? new Date(v.time_in).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '',
      v.time_out ? new Date(v.time_out).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `truck-tracker-${filterDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="dashboard">
      {/* Header */}
      <header className="header">
        <div className="header-left">
          <TruckIcon size={24} />
          <h1>Truck Tracker</h1>
        </div>
        <div className="header-right">
          {!demoMode && userRole && canEdit(userRole) && (
            <button className="btn btn-secondary btn-sm" onClick={exportCSV} title="Download CSV">
              <Download size={16} /> Export
            </button>
          )}
          {!demoMode && userRole && canEdit(userRole) && (
            <button className="btn btn-primary" onClick={() => setShowForm(true)}>
              <Plus size={16} /> Add Truck
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={onLogout}>
            <LogOut size={16} /> {demoMode ? 'Exit Demo' : 'Logout'}
          </button>
        </div>
      </header>
      {!demoMode && userRole === 'admin' && (
        <button className="btn btn-ghost btn-sm" onClick={onAdmin}>
          <Settings size={16} /> Admin
        </button>
      )}
      {demoMode && (
        <div className="demo-banner">
          <span>🔒 Demo Mode — Read Only</span>
          <span className="demo-hint">Sign in with magic link to enable editing</span>
        </div>
      )}

      {/* Stats */}
      <div className="stats">
        <div className="stat-card">
          <span className="stat-value">{todayVisits.length}</span>
          <span className="stat-label">Today</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{activeCount}</span>
          <span className="stat-label">Active</span>
        </div>
        <div className="stat-card stat-loaded">
          <span className="stat-value">{loadedCount}</span>
          <span className="stat-label">Loaded</span>
        </div>
        <div className="stat-card stat-issue">
          <span className="stat-value">{issueCount}</span>
          <span className="stat-label">Issues</span>
        </div>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="filter-row">
          <div className="filter-group">
            <Calendar size={16} />
            <input type="date" className="input input-sm" value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)} />
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowFilters(!showFilters)}>
            <Filter size={16} /> Status <ChevronDown size={14} />
          </button>
        </div>
        {showFilters && (
          <div className="status-filter-bar">
            <button className={`status-chip ${filterStatus === 'all' ? 'active' : ''}`}
              onClick={() => setFilterStatus('all')}>All</button>
            {STATUS_ORDER.map(s => (
              <button key={s} className={`status-chip ${filterStatus === s ? 'active' : ''}`}
                style={{ '--chip-color': STATUS_COLORS[s] } as React.CSSProperties}
                onClick={() => setFilterStatus(s)}>
                {STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading"><div className="spinner" /> Loading...</div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Truck</th>
                <th>Plant</th>
                <th>Destination</th>
                <th>Company</th>
                <th>Status</th>
                <th>Time In</th>
                <th>Time Out</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredVisits.length === 0 && (
                <tr>
                  <td colSpan={8} className="empty-state">
                    <TruckIcon size={48} />
                    <p>No truck entries found</p>
                    <button className="btn btn-primary" onClick={() => setShowForm(true)}>
                      <Plus size={16} /> Add First Entry
                    </button>
                  </td>
                </tr>
              )}
              {filteredVisits.map(v => (
                <React.Fragment key={v.id}>
                  <tr className={`visit-row ${expanded === v.id ? 'expanded' : ''}`}
                    onClick={() => setExpanded(expanded === v.id ? null : v.id)}>
                    <td className="truck-cell">{v.trucks?.truck_no || '—'}</td>
                    <td>{v.plants?.code || '—'}</td>
                    <td>{v.destination}</td>
                    <td>{v.logistic_companies?.name || '—'}</td>
                    <td><StatusBadge status={v.status} /></td>
                    <td>{v.time_in ? new Date(v.time_in).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                    <td>{v.time_out ? new Date(v.time_out).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                    <td>
                      <button className="icon-btn" onClick={(e) => { e.stopPropagation(); setEditVisit(v); setShowForm(true); }}>
                        ✏️
                      </button>
                    </td>
                  </tr>
                  {expanded === v.id && (
                    <tr className="expand-row">
                      <td colSpan={8}>
                        <div className="expand-details">
                          <div><strong>Contact:</strong> {v.contact_no || '—'}</div>
                          <div><strong>Driver:</strong> {v.driver_name || '—'}</div>
                          <div><strong>Forklift Operator:</strong> {v.forklift_operator_name || '—'}</div>
                          <div><strong>Shift:</strong> {v.shift_loaded || '—'}</div>
                          <div><strong>Registered:</strong> {new Date(v.registered_at).toLocaleString('en-IN')}</div>
                          {v.issue_note && <div className="issue-note"><strong>Issue:</strong> {v.issue_note}</div>}
                        </div>
                      </td>
                    </tr>
                  )}
                  {!demoMode && (
                    <React.Fragment key={v.id + '-edit'}>
                      <td colSpan={8} className="edit-row">
                        <button className="btn btn-sm btn-ghost" onClick={(e) => { e.stopPropagation(); setEditVisit(v); setShowForm(true); }}>
                          ✏️ Edit
                        </button>
                      </td>
                    </React.Fragment>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <EntryForm
          plants={plants}
          logisticCompanies={logisticCompanies}
          trucks={trucks}
          onClose={closeForm}
          onSaved={handleSaved}
          editVisit={editVisit}
          userRole={userRole}
        />
      )}
    </div>
  );
}

// ------------------ Admin: Plants Management ------------------
function AdminPlants({ onBack }: { onBack: () => void }) {
  const [plants, setPlants] = useState<Plant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editPlant, setEditPlant] = useState<Plant | null>(null);
  const [form, setForm] = useState({ code: '', name: '', address: '', timezone: 'Asia/Kolkata' });
  const [saving, setSaving] = useState(false);

  const loadPlants = useCallback(async () => {
    const { data } = await supabase.from('plants').select('*').order('code');
    if (data) setPlants(data);
    setLoading(false);
  }, []);

  useEffect(() => { loadPlants(); }, [loadPlants]);

  const handleSave = async () => {
    if (!form.code || !form.name) { alert('Code and Name required'); return; }
    setSaving(true);
    const payload = { code: form.code.toUpperCase(), name: form.name, address: form.address, timezone: form.timezone };
    const { error } = editPlant
      ? await supabase.from('plants').update(payload).eq('id', editPlant.id)
      : await supabase.from('plants').insert(payload);
    setSaving(false);
    if (error) alert('Error: ' + error.message);
    else { loadPlants(); setShowForm(false); setEditPlant(null); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this plant?')) return;
    const { error } = await supabase.from('plants').delete().eq('id', id);
    if (error) alert('Error: ' + error.message);
    else loadPlants();
  };

  const openForm = (plant?: Plant) => {
    if (plant) {
      setEditPlant(plant);
      setForm({ code: plant.code, name: plant.name, address: plant.address || '', timezone: plant.timezone || 'Asia/Kolkata' });
    } else {
      setEditPlant(null);
      setForm({ code: '', name: '', address: '', timezone: 'Asia/Kolkata' });
    }
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditPlant(null); };

  if (loading) return <div className="loading"><div className="spinner" /> Loading...</div>;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <button className="btn btn-ghost btn-sm" onClick={onBack}><ArrowLeft size={16} /> Back</button>
        <h1>Plants Management</h1>
      </div>
      <button className="btn btn-primary" onClick={() => openForm()}><Plus size={16} /> Add Plant</button>
      <div className="table-container">
        <table className="table">
          <thead>
            <tr><th>Code</th><th>Name</th><th>Address</th><th>Timezone</th><th></th></tr>
          </thead>
          <tbody>
            {plants.length === 0 && <tr><td colSpan={5} className="empty-state"><Building size={48} /><p>No plants yet</p></td></tr>}
            {plants.map(p => (
              <tr key={p.id}>
                <td className="truck-cell">{p.code}</td>
                <td>{p.name}</td>
                <td>{p.address || '—'}</td>
                <td>{p.timezone}</td>
                <td>
                  <button className="icon-btn" onClick={() => openForm(p)}><EditIcon size={16} /></button>
                  <button className="icon-btn" onClick={() => handleDelete(p.id)} style={{color: 'var(--danger)'}}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2>{editPlant ? 'Edit Plant' : 'Add Plant'}</h2><button className="icon-btn" onClick={closeForm}><X size={20} /></button></div>
            <div className="form-grid">
              <div className="field"><label>Code</label><input type="text" className="input" value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})} disabled={!!editPlant} /></div>
              <div className="field"><label>Name</label><input type="text" className="input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
              <div className="field full-width"><label>Address</label><input type="text" className="input" value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
              <div className="field"><label>Timezone</label><input type="text" className="input" value={form.timezone} onChange={e => setForm({...form, timezone: e.target.value})} /></div>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={closeForm}>Cancel</button><button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : editPlant ? 'Update' : 'Add Plant'}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------ Admin: Logistic Companies Management ------------------
function AdminCompanies({ onBack }: { onBack: () => void }) {
  const [companies, setCompanies] = useState<LogisticCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editCompany, setEditCompany] = useState<LogisticCompany | null>(null);
  const [form, setForm] = useState({ name: '', contact_person: '', phone: '', email: '' });
  const [saving, setSaving] = useState(false);

  const loadCompanies = useCallback(async () => {
    const { data } = await supabase.from('logistic_companies').select('*').order('name');
    if (data) setCompanies(data);
    setLoading(false);
  }, []);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  const handleSave = async () => {
    if (!form.name) { alert('Name required'); return; }
    setSaving(true);
    const payload = { name: form.name, contact_person: form.contact_person, phone: form.phone, email: form.email };
    const { error } = editCompany
      ? await supabase.from('logistic_companies').update(payload).eq('id', editCompany.id)
      : await supabase.from('logistic_companies').insert(payload);
    setSaving(false);
    if (error) alert('Error: ' + error.message);
    else { loadCompanies(); setShowForm(false); setEditCompany(null); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this company?')) return;
    const { error } = await supabase.from('logistic_companies').delete().eq('id', id);
    if (error) alert('Error: ' + error.message);
    else loadCompanies();
  };

  const openForm = (company?: LogisticCompany) => {
    if (company) {
      setEditCompany(company);
      setForm({ name: company.name, contact_person: company.contact_person || '', phone: company.phone || '', email: company.email || '' });
    } else {
      setEditCompany(null);
      setForm({ name: '', contact_person: '', phone: '', email: '' });
    }
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditCompany(null); };

  if (loading) return <div className="loading"><div className="spinner" /> Loading...</div>;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <button className="btn btn-ghost btn-sm" onClick={onBack}><ArrowLeft size={16} /> Back</button>
        <h1>Logistic Companies</h1>
      </div>
      <button className="btn btn-primary" onClick={() => openForm()}><Plus size={16} /> Add Company</button>
      <div className="table-container">
        <table className="table">
          <thead>
            <tr><th>Name</th><th>Contact Person</th><th>Phone</th><th>Email</th><th></th></tr>
          </thead>
          <tbody>
            {companies.length === 0 && <tr><td colSpan={5} className="empty-state"><Building2 size={48} /><p>No companies yet</p></td></tr>}
            {companies.map(c => (
              <tr key={c.id}>
                <td className="truck-cell">{c.name}</td>
                <td>{c.contact_person || '—'}</td>
                <td>{c.phone || '—'}</td>
                <td>{c.email || '—'}</td>
                <td>
                  <button className="icon-btn" onClick={() => openForm(c)}><EditIcon size={16} /></button>
                  <button className="icon-btn" onClick={() => handleDelete(c.id)} style={{color: 'var(--danger)'}}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h2>{editCompany ? 'Edit Company' : 'Add Company'}</h2><button className="icon-btn" onClick={closeForm}><X size={20} /></button></div>
            <div className="form-grid">
              <div className="field"><label>Name</label><input type="text" className="input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
              <div className="field"><label>Contact Person</label><input type="text" className="input" value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} /></div>
              <div className="field"><label>Phone</label><input type="tel" className="input" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
              <div className="field"><label>Email</label><input type="email" className="input" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
            </div>
            <div className="modal-footer"><button className="btn btn-secondary" onClick={closeForm}>Cancel</button><button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : editCompany ? 'Update' : 'Add Company'}</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------ Admin Dashboard ------------------
function AdminDashboard({ onBack }: { onBack: () => void }) {
  return (
    <div className="admin-page">
      <div className="admin-header">
        <button className="btn btn-ghost btn-sm" onClick={onBack}><ArrowLeft size={16} /> Back to Dashboard</button>
        <h1>Administration</h1>
      </div>
      <div className="admin-cards">
        <button className="admin-card" onClick={() => window.dispatchEvent(new CustomEvent('admin-nav', { detail: 'plants' }))}>
          <Building size={32} /><h3>Plants</h3><p>Manage warehouse locations</p>
        </button>
        <button className="admin-card" onClick={() => window.dispatchEvent(new CustomEvent('admin-nav', { detail: 'companies' }))}>
          <Building2 size={32} /><h3>Logistic Companies</h3><p>Manage carrier companies</p>
        </button>
      </div>
    </div>
  );
}

// ------------------ App ------------------
export default function App() {
  const [session, setSession] = useState<any>(null);
  const [demoMode, setDemoMode] = useState(false);
  const [userProfile, setUserProfile] = useState<Profile | null>(null);
  const [adminView, setAdminView] = useState<'dashboard' | 'plants' | 'companies' | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setSession(session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session?.user?.id) {
        supabase.from('profiles').select('*').eq('id', session.user.id).single()
          .then(({ data }) => setUserProfile(data));
      } else {
        setUserProfile(null);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const handler = (e: CustomEvent) => setAdminView(e.detail);
    window.addEventListener('admin-nav', handler as EventListener);
    return () => window.removeEventListener('admin-nav', handler as EventListener);
  }, []);

  const isAdmin = userProfile?.role === 'admin';

  if (!session && !demoMode) {
    return <AuthPage onDemo={() => setDemoMode(true)} />;
  }

  // Admin views
  if (adminView === 'plants') return <AdminPlants onBack={() => setAdminView('dashboard')} />;
  if (adminView === 'companies') return <AdminCompanies onBack={() => setAdminView('dashboard')} />;

  return <Dashboard onLogout={() => { supabase.auth.signOut(); setDemoMode(false); }} demoMode={demoMode} isAdmin={isAdmin} onAdmin={() => setAdminView('dashboard')} />;
}